/* eslint-disable react/prop-types */
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useEffect, useId, useState } from 'react';
import { gatewayUrl } from './../gateway';
import {
    APTITUDES,
    ATTACHMENTS,
    CUSTOM_PREFIX,
    DESCRIPTION,
    EDTECH_ASSESSMENT_DESCRIPTION,
    EDTECH_ASSESSMENT_HAS_GROUP_EVALUATION,
    EDTECH_ASSESSMENT_HAS_GROUP_PARTICIPATION,
    EDTECH_ASSESSMENT_OUTPUT,
    EDTECH_ASSESSMENT_TYPE,
    EDTECH_CUSTOM_RESULT,
    EDTECH_FRAMEWORK_ALIGNMENT,
    EDTECH_ISSUER_ADDRESS,
    EDTECH_ISSUER_CONTACT_INSTRUCTIONS,
    EDTECH_ISSUER_NAME,
    EDTECH_ISSUER_URL,
    GENERAL_PERSONALIZED_INFORMATION,
    REQUIREMENTS,
} from '@pok/template-manager-sdk';
import HtmlTooltip from './HtmlTooltip.jsx';
import InformationIcon from './InformationIcon.jsx';
import DownloadIcon from './DownloadIcon.jsx';

export default function AdditionalInformationPreview({
    template,
    t,
    gateway,
    assets,
    validated,
    goToValidateCallback,
}) {
    const [canDownload, setCanDownload] = useState(false);
    const info = template?.info || [];

    const findIn = (key) => info.find((x) => x.key === key)?.value;
    const description = findIn(DESCRIPTION);

    const requirements = findIn(REQUIREMENTS);
    const aptitudes = findIn(APTITUDES);
    const generalInfo = findIn(GENERAL_PERSONALIZED_INFORMATION);
    const attachments = findIn(ATTACHMENTS);
    const personalInfo = info
        .filter((x) => x.key)
        .filter((i) => i.key.startsWith(CUSTOM_PREFIX))
        .filter((k) => k.key !== EDTECH_CUSTOM_RESULT);
    // edtech custom fields
    const resultEdTech = info.find((x) => x.key === EDTECH_CUSTOM_RESULT);
    const framework = findIn(EDTECH_FRAMEWORK_ALIGNMENT);
    const issuerName = findIn(EDTECH_ISSUER_NAME);
    const evaluationType = findIn(EDTECH_ASSESSMENT_TYPE);

    useEffect(() => {
        const isDownloadable = validated && assets.every((x) => x.fileSource !== undefined);
        setCanDownload(isDownloadable);
    }, [validated, assets]);

    const supportedTypes = {
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        pdf: 'application/pdf',
    };

    function mimeFrom(filename) {
        return supportedTypes[filename.split('.').pop().toLowerCase()] || 'application/octet-stream';
    }

    const downloadFromAttachment = async (attachment) => {
        const url = gatewayUrl(`ipfs://${attachment.value}`, gateway);
        const response = await fetch(url);
        const blob = await response.blob();
        const file = new File([blob], attachment.name, { type: mimeFrom(attachment.name) });
        return download(file, attachment.name);
    };

    const download = async (file, downloadName) => {
        var link = document.createElement('a');
        link.href = window.URL.createObjectURL(file);
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
    };

    function textWithNewlines(text) {
        if (!text) return '';
        const parts = text.split('\n');

        return parts?.map((part, index) => (
            <React.Fragment key={index}>
                <Linkify text={part} />
                {index !== parts.length - 1 && <br />}
            </React.Fragment>
        ));
    }

    return (
        <>
            {info && (
                <Stack alignItems={'start'} minHeight={400}>
                    {framework && (
                        <>
                            <Typography variant="subtitle1" pt={2}>
                                {t('design.info.framework_alignment')}
                            </Typography>
                            <Typography variant="body2" pt={1}>
                                <Linkify text={framework} />
                            </Typography>
                        </>
                    )}
                    {description && (
                        <>
                            <Typography variant="subtitle1" pt={2}>
                                {t('design.info.description')}
                            </Typography>
                            <Typography variant="body2" pt={1}>
                                {textWithNewlines(description)}
                            </Typography>
                        </>
                    )}

                    {aptitudes && aptitudes.length > 0 && (
                        <>
                            <Typography variant="subtitle1" pt={3}>
                                {t('design.info.aptitude')}
                            </Typography>
                            <Stack
                                direction={'row'}
                                flexWrap={'wrap'}
                                sx={{
                                    width: '100%',
                                }}
                            >
                                {aptitudes?.map((x, i) => (
                                    <Chip
                                        sx={{
                                            background: '#F6F6F6',
                                            border: `1px solid #CCC9D1`,
                                            m: 1,
                                            ml: 0,
                                            minHeight: '32px',
                                            height: 'auto',
                                        }}
                                        label={
                                            <Typography
                                                sx={{
                                                    wordWrap: 'break-word',
                                                    wordBreak: 'keep-all',
                                                    whiteSpace: 'normal',
                                                    fontSize: { xs: '11px', md: '12px' },
                                                    fontWeight: '500',
                                                    color: '#1E1E1E',
                                                }}
                                            >
                                                {x}
                                            </Typography>
                                        }
                                        key={`aptitude_${i}`}
                                    />
                                ))}
                            </Stack>
                        </>
                    )}

                    {requirements && (
                        <>
                            <Typography variant="subtitle1" pt={3}>
                                {t('design.info.requirements')}
                            </Typography>
                            <Typography variant="body2" pt={1}>
                                {textWithNewlines(requirements)}
                            </Typography>
                        </>
                    )}

                    {issuerName && (
                        <>
                            <Typography variant="subtitle1" pt={3} mb={1}>
                                {t('design.info.issuer_details')}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                                {findIn(EDTECH_ISSUER_NAME)}
                            </Typography>
                            <Typography variant="body2">{findIn(EDTECH_ISSUER_ADDRESS)}</Typography>
                            <Typography variant="body2">{<Linkify text={findIn(EDTECH_ISSUER_URL)} />} </Typography>
                            <Typography variant="body2">{findIn(EDTECH_ISSUER_CONTACT_INSTRUCTIONS)}</Typography>
                        </>
                    )}
                    {evaluationType && (
                        <>
                            <Typography variant="subtitle1" pt={3} mb={1}>
                                {t('design.info.evaluation')}
                            </Typography>
                            <Stack direction={'row'} mb={1} spacing={1}>
                                <Typography variant="body2" fontWeight={700}>
                                    {t('design.info.evaluation_type')}:
                                </Typography>
                                <Typography variant="body2">
                                    {t(`design.info.evaluation_type_${evaluationType}`)}
                                </Typography>
                            </Stack>
                            <Stack direction={'row'} mb={1} spacing={1}>
                                <Typography variant="body2" fontWeight={700}>
                                    {t('design.info.evaluation_description')}:
                                </Typography>
                                <Typography variant="body2">{findIn(EDTECH_ASSESSMENT_DESCRIPTION)}</Typography>
                            </Stack>
                            <Stack direction={'row'} mb={1} spacing={1}>
                                <Typography variant="body2" fontWeight={700}>
                                    {t('design.info.evaluation_details')}:
                                </Typography>
                                <Typography variant="body2">{findIn(EDTECH_ASSESSMENT_OUTPUT)}</Typography>
                            </Stack>
                            {(findIn(EDTECH_ASSESSMENT_HAS_GROUP_EVALUATION) ||
                                findIn(EDTECH_ASSESSMENT_HAS_GROUP_PARTICIPATION)) && (
                                <ul>
                                    {findIn(EDTECH_ASSESSMENT_HAS_GROUP_EVALUATION) && (
                                        <li>
                                            <Typography variant="body2">
                                                {t('design.info.evaluation_group_evaluation')}
                                            </Typography>
                                        </li>
                                    )}
                                    {findIn(EDTECH_ASSESSMENT_HAS_GROUP_PARTICIPATION) && (
                                        <li>
                                            <Typography variant="body2">
                                                {t('design.info.evaluation_group_participation')}
                                            </Typography>
                                        </li>
                                    )}
                                </ul>
                            )}
                        </>
                    )}
                    {resultEdTech && (
                        <Stack direction={'row'} pt={3} mb={1} spacing={1} alignItems={'center'}>
                            <Typography variant="subtitle1" fontWeight={700}>
                                {t('design.info.custom_edtech_result')}
                            </Typography>
                            <Typography variant="body2">
                                {template.params.find((m) => m.name === EDTECH_CUSTOM_RESULT)?.value}
                            </Typography>
                        </Stack>
                    )}
                    {generalInfo && (
                        <>
                            {generalInfo.map((x) => (
                                <React.Fragment key={x.key}>
                                    <Typography variant="subtitle1" pt={3}>
                                        <Linkify text={x.text} />
                                    </Typography>
                                    <Typography variant="body2" pt={1}>
                                        {textWithNewlines(x.value)}
                                    </Typography>
                                </React.Fragment>
                            ))}
                        </>
                    )}

                    {personalInfo && (
                        <>
                            {personalInfo.map((x) => (
                                <React.Fragment key={x.key}>
                                    <Typography variant="subtitle1" pt={3}>
                                        <Linkify text={x.value} />
                                    </Typography>
                                    <Typography variant="body2" pt={1}>
                                        {textWithNewlines(template.params.find((m) => m.name === x.key)?.value)}
                                    </Typography>
                                </React.Fragment>
                            ))}
                        </>
                    )}
                    {attachments && attachments.length > 0 && (
                        <Stack spacing={1} pt={2}>
                            <Typography variant="subtitle1">{t('design.info.documents')}</Typography>

                            {attachments.map((a) => (
                                <React.Fragment key={a.value}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<DownloadIcon fill={'#5124A6'} />}
                                        sx={{ background: '#FDF3FF', borderRadius: 2, border: '2px solid #5124A6' }}
                                        onClick={() => downloadFromAttachment(a)}
                                    >
                                        {a.name}
                                    </Button>
                                </React.Fragment>
                            ))}
                        </Stack>
                    )}
                    {(assets?.length ?? 0) > 0 && (
                        <Stack spacing={1} pt={2}>
                            <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                <Typography variant="subtitle1">{t('design.info.files')}</Typography>
                                <HtmlTooltip text={t('design.info.files_tooltip')}>
                                    <InformationIcon white={false} />
                                </HtmlTooltip>
                            </Stack>

                            <Stack direction={'row'} spacing={1}>
                                <Typography variant="body11">{t('design.info.files_description')}</Typography>
                                {!validated && (
                                    <Typography
                                        onClick={goToValidateCallback}
                                        variant="body11"
                                        sx={{ color: '#548CFF', cursor: 'pointer' }}
                                    >
                                        {t('design.info.files_description2')}
                                    </Typography>
                                )}
                            </Stack>
                            <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                {assets.map((a) => (
                                    <React.Fragment key={useId}>
                                        <Button
                                            disabled={!canDownload}
                                            className={canDownload ? 'btn_enabled' : 'btn_disabled'}
                                            startIcon={<DownloadIcon fill={canDownload ? '#5124A6' : '#CCC9D1'} />}
                                            onClick={() => (canDownload ? download(a.fileSource, a.name) : {})}
                                        >
                                            {a.name}
                                        </Button>
                                    </React.Fragment>
                                ))}
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            )}
        </>
    );
}

const Linkify = ({ text }) => {
    if (typeof text !== 'string') return text;

    const urlRegex = /(https?:\/\/[^\s]+)/g;

    const parts = text.split(urlRegex);
    return (
        <>
            {parts.map((part, index) =>
                part.match(urlRegex) ? (
                    <a key={index} href={part} style={{ color: '#548CFF' }} target="_blank" rel="noopener noreferrer">
                        {part}
                    </a>
                ) : (
                    part
                ),
            )}
        </>
    );
};
